import { Box, TextHeader, TicketTable, Typography } from '@components';
import {
  AssetsService,
  CompanyConfiguration,
  ConfigurationService,
  CouponService,
  LicenseService,
  ProjectsService,
  TicketService,
  UsersService,
} from '@services';
import React, { FC, useEffect, useState } from 'react';
import { FilePlus } from 'react-feather';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import {
  UserCoupon,
  UserHardware,
  UserLicense,
  UserProjects,
  UserRequestedLicenseTable,
} from '@containers';
import { RootState, setLicenseRequests, setRootAside } from '@stores';
import { CompanyModules, hasModule } from '@utils';
import SimGraphs from './SimGraphs';

export const UserDashboard: FC<{ userId: number }> = ({ userId }) => {
  const { t } = useTranslation();
  const [dashboardData, setDashboardData] = useState(null);
  const [phoneNumbers, setPhoneNumbers] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [configurations, setConfigurations] = useState<
    Record<string, CompanyConfiguration>
  >({});
  const dispatch = useDispatch();

  const profileState = useSelector((state: RootState) => state.profile);
  const role = useSelector((state: RootState) => state.auth.roles[0]);
  const userRequestedLicenses = useSelector(
    (state: RootState) => state.licenseRequest.licenseRequests
  );
  const companyId = useSelector(
    (state: RootState) => state.companies.selectedCompany?.id
  );
  const modules = useSelector((state: RootState) => state.auth.modules);

  const userService = new UsersService();
  const ticketService = new TicketService();
  const projectService = new ProjectsService();
  const assetService = new AssetsService();
  const couponService = new CouponService();
  const licenseService = new LicenseService();
  const configService = new ConfigurationService();

  useEffect(() => {
    setLoading(true);

    const fetchConfigurations = async () => {
      try {
        const configs = await configService.getCompanyConfigurations(companyId);
        const configMap: Record<string, CompanyConfiguration> = {};
        configs.result.forEach((config: CompanyConfiguration) => {
          configMap[config.option] = config;
        });
        setConfigurations(configMap);

        await fetchAll(configMap);
      } catch (error) {
        console.error('Error fetching configurations:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchConfigurations();
  }, [companyId, userId]);

  const fetchAll = async (configMap: Record<string, CompanyConfiguration>) => {
    const fetchPromises = [
      ticketService.getUserTickets(userId, companyId),
      projectService.getUserProjects(userId, companyId),
    ];

    if (hasModule(modules, CompanyModules.CYOD)) {
      fetchPromises.push(couponService.getUserCoupon(userId, companyId));
    } else {
      fetchPromises.push(Promise.resolve(null));
    }

    if (
      (configMap['usage']?.option_value || role?.role === 'admin') &&
      hasModule(modules, CompanyModules.TELECOM) &&
      hasModule(modules, CompanyModules.RAPPORTAGE)
    ) {
      fetchPromises.push(userService.getUserSims(companyId, userId));
    } else {
      fetchPromises.push(Promise.resolve(null));
    }

    if (
      (configMap['hardware']?.option_value || role?.role === 'admin') &&
      hasModule(modules, CompanyModules.HARDWARE)
    ) {
      fetchPromises.push(assetService.getUserAssets(companyId, userId));
    } else {
      fetchPromises.push(Promise.resolve(null));
    }

    if (configMap['licenses']?.option_value || role?.role === 'admin') {
      fetchPromises.push(licenseService.getUserLicenses(companyId, userId));
      fetchPromises.push(
        licenseService.getUserRequestedLicenseByUserId(userId, companyId)
      );
    } else {
      fetchPromises.push(Promise.resolve(null));
      fetchPromises.push(Promise.resolve(null));
    }

    const results = await Promise.allSettled(fetchPromises);
    const [
      userTickets,
      userProjects,
      userCoupon,
      userSims,
      userAssets,
      userLicenses,
      userRequestedLicenses,
    ] = results.map((r) => (r.status === 'fulfilled' ? r.value : null));

    setDashboardData({
      userTickets: userTickets ?? [],
      userProjects: userProjects ?? [],
      userCoupon: userCoupon ?? 0,
      userSims: userSims ?? [],
      userAssets: userAssets ?? null,
      userLicenses: userLicenses ?? null,
    });
    dispatch(setLicenseRequests(userRequestedLicenses ?? null));

    const phoneNumbers =
      userSims?.sim?.map((sim) => sim.sim_gsm_nr).filter(Boolean) || [];
    setPhoneNumbers(phoneNumbers);
  };

  const requestLicense = () => {
    dispatch(
      setRootAside({
        component: 'requestLicense',
        data: { license: {} },
        title: 'Licentie aanvragen',
      })
    );
  };

  return (
    <div className="space-y-3">
      <TextHeader title={'Dashboard'} />
      {loading ? (
        <div className="mx-auto text-xl">
          {t('Dashboard is aan het laden...')}
        </div>
      ) : (
        <Box gridSize={'grid-6'} type={'noStyle'}>
          {hasModule(modules, CompanyModules.CYOD) && (
            <div className="col-span-3 md:col-span-1 -order-2 border dark:border-slate-800 rounded-lg p-3 h-full w-full">
              <UserCoupon coupon={dashboardData?.userCoupon ?? 0} />
            </div>
          )}
          {(configurations['usage']?.option_value || role?.role === 'admin') &&
          hasModule(modules, CompanyModules.TELECOM) &&
          hasModule(modules, CompanyModules.RAPPORTAGE) ? (
            <div className="col-span-3 2xl:col-span-5 2xl:-order-1 border dark:border-slate-800 rounded-lg p-3 h-full">
              {phoneNumbers.length === 0 ? (
                <div className="h-full flex justify-center items-center">
                  <Typography tag={'h1'} type={'title'}>
                    Geen sim data
                  </Typography>
                </div>
              ) : (
                <SimGraphs phoneNumbers={phoneNumbers} />
              )}
            </div>
          ) : null}

          <div className="col-span-3 border dark:border-slate-800 rounded-lg p-3 h-full">
            {!dashboardData.userTickets.result ||
            dashboardData.userTickets.result.length === 0 ? (
              <div className="h-full flex justify-center items-center">
                <Typography tag={'h1'} type={'title'}>
                  Geen tickets
                </Typography>
              </div>
            ) : (
              <TicketTable
                tickets={dashboardData?.userTickets?.result?.slice(0, 3) || []}
              />
            )}
          </div>

          {(configurations['hardware']?.option_value ||
            role?.role === 'admin') &&
          hasModule(modules, CompanyModules.HARDWARE) ? (
            <div className="col-span-3 border dark:border-slate-800 rounded-lg p-3 h-full">
              {dashboardData?.userAssets?.length === 0 ? (
                <div className="h-full flex justify-center items-center">
                  <Typography tag={'h1'} type={'title'}>
                    Geen activa
                  </Typography>
                </div>
              ) : (
                <UserHardware assets={dashboardData?.userAssets} />
              )}
            </div>
          ) : null}

          <div className="col-span-3 2xl:col-span-4 border dark:border-slate-800 rounded-lg p-3 h-full">
            {dashboardData?.userProjects?.length === 0 ? (
              <div className="h-full flex justify-center items-center">
                <Typography tag={'h1'} type={'title'}>
                  Geen projecten
                </Typography>
              </div>
            ) : (
              <UserProjects projects={dashboardData?.userProjects} />
            )}
          </div>

          {configurations['licenses']?.option_value ||
          role?.role === 'admin' ? (
            <div className="col-span-3 md:col-span-2 md:-order-1 2xl:order-6 border dark:border-slate-800 rounded-lg p-3 h-full relative">
              {Number(userId) === profileState.userId && (
                <button
                  className="absolute bottom-2 right-2"
                  onClick={() => requestLicense()}
                >
                  <div className="inline-flex items-center justify-center w-9 h-9 overflow-hidden rounded-full border bg-white border-gray-300 hover:bg-gray-100 dark:bg-slate-900 dark:border-gray-700 dark:hover:bg-slate-700">
                    <FilePlus size={18} />
                  </div>
                </button>
              )}
              {dashboardData?.userLicenses?.length === 0 ? (
                <div className="flex justify-center items-center">
                  <Typography tag={'h1'} type={'title'}>
                    Geen licenties
                  </Typography>
                </div>
              ) : (
                <UserLicense licenses={dashboardData?.userLicenses} />
              )}
              {userRequestedLicenses?.length !== 0 && (
                <UserRequestedLicenseTable
                  userRequestedLicenses={userRequestedLicenses}
                />
              )}
            </div>
          ) : null}
        </Box>
      )}
    </div>
  );
};

export default UserDashboard;
